import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { PrimitiveAtom, useAtom, useSetAtom } from "jotai";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import { Button, Flex, Spinner, Text, Toggle } from "src/elements";
import { useTranslation } from "src/i18n";
import { downloadFile } from "src/utils/downloadFile";
import {
  describeShipFailure,
  shipCdtCompression,
  unplacedCombosOf,
  unplacedProductCodesOf,
} from "../store/cdtCompressionService";
import type { UnplacedCdtCombo } from "@CommonApi/merchflows/_merchflowId_/cdt/compression";
import { saveVariantRestrictions } from "../store/variantRestrictionService";
import {
  agreeToRestrictions,
  applyRestrictionsThenGenerate,
  declineRestrictions,
  proposeRestrictionsForAll,
  type RestrictionContribution,
  type RestrictionProposal,
} from "../domain/variantRestrictionUpload";
import { buildRestrictionOffers, placementRuleIdOf } from "../domain/restrictionOffer";
import { applyPromotion } from "../domain/cdtLayout/restrictionRemedy";
import { applyPromotion as adoptPromotion, revertPromotion } from "../domain/wizardPromotion";
import { prepareCompressionPayload } from "../adapters/toCompressionPayload";
import { layoutToPog } from "../adapters/layoutToPog";
import { renderedProjectionOf } from "../adapters/renderedLayout";
import { pogToStudioItems } from "../adapters/pogToStudioItems";
import { toCdtAssignmentsCsv } from "../adapters/toCdtAssignmentsCsv";
import {
  restrictionsToFileRows,
  toRestrictionsDetailCsv,
  toVariantRestrictionsFileCsv,
} from "../adapters/toRestrictionsCsv";
import { finalizeCdts } from "../domain/finalize";
import { adoptMeasuredBands } from "../domain/adoptMeasuredBands";
import { cdt0ResolutionOf } from "../domain/cdt0Resolution";
import { acceptFlag, applyFix, dismissFlag } from "../domain/studioTransactions";
import {
  applyHierarchyMove,
  budgetQuestionOf,
  siblingMoveOf,
  type MoveReport,
} from "../domain/hierarchyMoves/applyHierarchyMove";
import { poolingKeyOf } from "../domain/cdtLayout/buildFromCdt";
import { poolingQuestionOf } from "../domain/cdtLayout/poolingQuestion";
import {
  consumePreviewOnClose,
  getPanelPreviewState,
  initPanelPreviewState,
  resetPanelPreviewState,
  setPanelPreviewOn,
  setPanelRegenerated,
} from "../store/panelPreviewState";
import { historyPreviewFollowUpFor, pogAuthorOf, shouldStandDown } from "../store/previewReconcile";
import { studioIsDirty } from "../store/studioDirty";
import { clearStudioHistory, studioHistoryKey } from "../store/studioHistoryStore";
import { useModals } from "src/components/Modals/store/hooks";
import { Color } from "src/elements/Color/Color";
import { buildLayout, normativeDraftOf } from "../domain/buildLayout";
import { fixVerticalBlocking, layoutFromItems } from "../domain/verticalBlockingFix";
import { detectExceptions } from "../resolver/detectExceptions";
import { clearResolverCache, useAutoResolve } from "../resolver/useAutoResolve";
import { useInference } from "../resolver/useInference";
import { ALERT_KINDS, type InferenceException } from "../resolver/inferenceExceptions";
import type { ResolverStep } from "../resolver/cdtResolve";
import { PATH_SEPARATOR } from "../domain/studioTypes";
import { shortFlagTitle } from "../domain/shortFlagTitle";
import type { Flag, FlagSeverity, GeneratedLayout, StudioDraft } from "../domain/studioTypes";
import { gateOwnershipLinesOf } from "../domain/gateOwnership";
import type { StudioTreeNode } from "../projections/treeProjection";
import {
  cdtStudioActionRequestAtom,
  cdtStudioExceptionCardsAtom,
  cdtStudioHighlightAtom,
  cdtStudioPinsAtom,
  cdtStudioSelectedFlagAtom,
  cdtStudioSelectionSourceAtom,
  sameHighlightIds,
  sameStudioPins,
  type CdtStudioExceptionCard,
  type CdtStudioPin,
} from "../store/atoms";
import { useStudioAttributesQuery } from "../store/useStudioAttributesQuery";
import { blockingFlagsOnly, useStudioSession } from "../store/useStudioSession";
import {
  namedStudioConfigurationOf,
  type NamedStudioConfiguration,
} from "../store/studioNamingProfile";
import {
  type StudioCategoryConfiguration,
  type StudioCategoryIdentity,
} from "../store/studioCategoryConfiguration";
import { STUDIO_THEME } from "../constants/studioTheme";
import { StudioTreeCanvas } from "./StudioTreeCanvas";

interface Props {
  pogAtom: PrimitiveAtom<Pog>;
  isEditable: boolean;
  /** Scopes the item master. Without it two flows share one cache entry. */
  merchflowId?: number;
  onClose?: () => void;
  onGenerate?: () => Promise<unknown>;
}

interface CdtStudioPanelProps extends Props {
  basePogId?: number;
  categoryIdentity?: StudioCategoryIdentity;
  categoryConfiguration: StudioCategoryConfiguration;
}

interface FinalizationLayoutSources {
  blockingFixLayout?: GeneratedLayout;
  regenerated: boolean;
  regeneratedLayout: GeneratedLayout;
  resolverHasChanges: boolean;
  resolverLayout: GeneratedLayout;
  sessionLayout: GeneratedLayout;
}

export const finalizationLayoutOf = ({
  blockingFixLayout,
  regenerated,
  regeneratedLayout,
  resolverHasChanges,
  resolverLayout,
  sessionLayout,
}: FinalizationLayoutSources): GeneratedLayout =>
  blockingFixLayout ??
  (regenerated ? regeneratedLayout : resolverHasChanges ? resolverLayout : sessionLayout);

export const currentBlockingFixResult = <T,>(
  state: { sourceDraft: StudioDraft; result: T } | null,
  draft: StudioDraft,
): T | null => (state?.sourceDraft === draft ? state.result : null);

export const staleRuleNamesOf = (
  stale: readonly { restriction: { cdtPath: readonly string[] } }[],
): string => {
  const names = stale
    .map((entry) => entry.restriction.cdtPath[entry.restriction.cdtPath.length - 1]?.trim() ?? "")
    .filter((name) => name !== "");
  return [...new Set(names)].join(", ");
};

const SeverityChip = styled.span<{ $severity: FlagSeverity | "resolved" }>`
  padding: 0.125rem 0.5rem;
  border-radius: 999px;
  border: 1px solid ${({ $severity }) => STUDIO_THEME.severity[$severity].border};
  background: ${({ $severity }) => STUDIO_THEME.severity[$severity].background};
  color: ${({ $severity }) => STUDIO_THEME.severity[$severity].text};
  font-size: 0.625rem;
  font-weight: 800;
  white-space: nowrap;
`;

const FindingRow = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  width: 100%;
  padding: 0.5rem;
  border: 1px solid ${({ $selected }) => ($selected ? STUDIO_THEME.primary : STUDIO_THEME.border)};
  border-radius: 0.375rem;
  background: ${STUDIO_THEME.canvasBackground};
  cursor: pointer;
  text-align: left;
`;

const FindingsScroller = styled(Flex)`
  flex-direction: column;
  flex-grow: 1;
  gap: 0.375rem;
  overflow-y: auto;
  min-height: 0;
  padding: 0 0.75rem 0.75rem 0.75rem;
`;

const FindingsRegion = styled(Flex)`
  flex: 0 0 40%;
  flex-direction: column;
  min-height: 8rem;
  max-height: 40%;
  overflow: hidden;
  border-top: 1px solid ${STUDIO_THEME.border};
  background: ${STUDIO_THEME.canvasBackground};
`;

const severityOrder: FlagSeverity[] = ["blocking", "toCheck", "toReview"];

/** Keyed by pogAtom in store/panelPreviewState: the true original pog survives tab switches. */
const StudioPanelSession = ({
  pogAtom,
  isEditable,
  merchflowId,
  basePogId,
  categoryId,
  onClose,
  onGenerate,
  configuration,
  storeCodes = [],
}: Props & {
  basePogId: number | null;
  categoryId: number | null;
  configuration: NamedStudioConfiguration;
  storeCodes?: readonly string[];
}) => {
  const { t } = useTranslation("cdtStudio");
  const { closeModal, openConfirmModal } = useModals();
  const [pog, setPog] = useAtom(pogAtom);
  const setHighlights = useSetAtom(cdtStudioHighlightAtom);

  // The studio's frozen input. On remount the atom may hold an applied preview, which must
  // NOT become the new original.
  const originalPogRef = useRef<Pog | null>(null);
  if (originalPogRef.current === null) {
    const persisted = initPanelPreviewState(pogAtom as object, pog);
    // With no preview standing, a changed atom was rewritten externally: adopt it as the
    // new original. With a preview standing the difference is ours, so leave it to close.
    const external =
      persisted.originalPog !== pog && !persisted.previewOn && !persisted.regenerated;
    if (external) {
      clearResolverCache(persisted.originalPog);
      originalPogRef.current = resetPanelPreviewState(pogAtom as object, pog).originalPog;
    } else {
      originalPogRef.current = persisted.originalPog;
    }
  }
  const originalPog = originalPogRef.current;
  const pogs = useMemo(() => [originalPog], [originalPog]);
  const {
    adapterOptions,
    inferenceOptions,
    aestheticsRules,
    hierarchyMoveRules,
    verticalBlockingOptions,
    semanticRoles,
  } = configuration;
  const session = useStudioSession(pogs, {
    adapterOptions,
    inferenceOptions,
    blockIdentityAttributes: semanticRoles.blockIdentity,
    quantityAttribute: semanticRoles.quantity,
    terminalVariantAttribute: semanticRoles.terminalVariant,
    declaredValues: configuration?.declaredValues,
    declaredLabels: configuration?.declaredLabels,
    basePogId,
  });
  const { items, runs, draft, commit, flags, settledFlagIds, tree, layout } = session;
  const { baseLayout } = session;
  const displayedBefore = session.undoDepth === 0 ? baseLayout : undefined;
  const renderedProjection = useMemo(() => renderedProjectionOf(pogs, items), [pogs, items]);

  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedFlagId, setSelectedFlagId] = useAtom(cdtStudioSelectedFlagAtom);
  const [selectionSource, setSelectionSource] = useAtom(cdtStudioSelectionSourceAtom);
  const setPins = useSetAtom(cdtStudioPinsAtom);
  const setCards = useSetAtom(cdtStudioExceptionCardsAtom);
  const [actionRequest, setActionRequest] = useAtom(cdtStudioActionRequestAtom);
  const [previewOn, setPreviewOnState] = useState(
    // Always start OFF: the atom already holds whatever the user last kept.
    () => false,
  );
  const setPreviewOn = (value: boolean) => {
    setPreviewOnState(value);
    setPanelPreviewOn(pogAtom as object, value);
  };

  const resolver = useAutoResolve(items, session.resolverInputs, flags, originalPog);
  const resolverRef = useRef(resolver);
  resolverRef.current = resolver;
  const inference = useInference(resolver.inputs);
  // The "Layout assumptions" list (inference exceptions) is withdrawn from the panel: the
  // merchant reads it as more violations. The exceptions still feed the resolver and tests.
  const shownLayoutAssumptions: InferenceException[] = [];
  const [selectedExceptionId, setSelectedExceptionId] = useState<string | null>(null);
  const [regenerated, setRegeneratedState] = useState(
    () => getPanelPreviewState(pogAtom as object)?.regenerated ?? false,
  );
  const setRegenerated = (value: boolean) => {
    setRegeneratedState(value);
    setPanelRegenerated(pogAtom as object, value);
  };

  const normativeDraft = useMemo(
    () => normativeDraftOf(draft, semanticRoles.quantity),
    [draft, semanticRoles.quantity],
  );

  const regenLayout = useMemo(() => {
    if (!regenerated) return layout;
    return buildLayout(items, normativeDraft, runs);
  }, [regenerated, items, normativeDraft, runs, layout, draft]);

  const poolingStatus = useMemo(
    () =>
      new Map(
        poolingQuestionOf(items, runs, draft).map((status) => [
          poolingKeyOf(status.attr, status.value),
          status,
        ]),
      ),
    [items, runs, draft],
  );

  const regeneratedItems = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        placements: (regenLayout.placements[item.skuId]?.segments ?? []).map((segment) => ({
          run: segment.run,
          shelf: segment.shelf,
          xCm: segment.xCm,
          widthCm: segment.hFacings * item.facingWidthCm,
          hFacings: segment.hFacings,
        })),
      })),
    [items, regenLayout],
  );
  const regenFlags = useMemo(
    () =>
      regenerated
        ? blockingFlagsOnly(
            detectExceptions(regeneratedItems, runs, normativeDraft, {
              gradientPins: false,
              quantityAttribute: semanticRoles.quantity,
              blockIdentityAttributes: semanticRoles.blockIdentity,
              verticalBlockShare: verticalBlockingOptions.blockShare,
              terminalVariantAttribute: semanticRoles.terminalVariant,
            }).flags,
          )
        : null,
    [
      regenerated,
      regeneratedItems,
      runs,
      normativeDraft,
      semanticRoles,
      verticalBlockingOptions.blockShare,
    ],
  );

  const [detectTick, setDetectTick] = useState(0);
  const [blockingFixNote, setBlockingFixNote] = useState<string | null>(null);
  /** Unanswered keeps Finalize disarmed; save failure cannot live on the closing modal. */
  const [gateOpen, setGateOpen] = useState(false);
  const [gateFailure, setGateFailure] = useState<string | null>(null);
  const gateGenerationRef = useRef(0);

  const [shipState, setShipState] = useState<
    | { status: "idle" }
    | { status: "shipping" }
    | {
        status: "shipped";
        text: string;
        draft: StudioDraft;
        rulesUnsaved: boolean;
      }
    | { status: "refused"; text: string; unplaced: UnplacedCdtCombo[] }
  >({ status: "idle" });
  const [shipNotices, setShipNotices] = useState<readonly string[]>([]);
  const [blockingFixState, setBlockingFixState] = useState<{
    sourceDraft: StudioDraft;
    result: ReturnType<typeof fixVerticalBlocking>;
  } | null>(null);
  const blockingFixLayout = currentBlockingFixResult(blockingFixState, draft);
  const setBlockingFixLayout = (result: ReturnType<typeof fixVerticalBlocking> | null) =>
    setBlockingFixState(result ? { sourceDraft: draft, result } : null);
  const [restrictionsRefusal, setRestrictionsRefusal] = useState<string | null>(null);
  const displayedDetection = useMemo(() => {
    if (pog === originalPog && detectTick === 0) return null;
    try {
      const displayed = pogToStudioItems([pog], adapterOptions);
      const detected = detectExceptions(displayed.items, displayed.runs, draft, {
        gradientPins: !regenerated,
        quantityAttribute: semanticRoles.quantity,
        blockIdentityAttributes: semanticRoles.blockIdentity,
        verticalBlockShare: verticalBlockingOptions.blockShare,
        terminalVariantAttribute: semanticRoles.terminalVariant,
      });
      return { ...detected, flags: blockingFlagsOnly(detected.flags) };
    } catch {
      return null;
    }
  }, [
    pog,
    originalPog,
    adapterOptions,
    draft,
    regenerated,
    detectTick,
    semanticRoles,
    verticalBlockingOptions.blockShare,
  ]);

  const activeFlags = displayedDetection?.flags ?? (regenerated && regenFlags ? regenFlags : flags);
  const pinFor = (flag: Flag) =>
    displayedDetection?.pinByFlagId[flag.id] ?? session.resolverInputs.pinByFlagId[flag.id];

  const levelAttrForFlag = (flag: Flag): string | null => {
    const scope = flag.scopePath.join(PATH_SEPARATOR);
    const node = session.tree.nodesById[scope];
    const levelIndex = node?.branchLevels[0];
    if (levelIndex !== undefined) return draft.spine[levelIndex]?.attr ?? null;
    const roots = session.tree.rootIds.map((id) => session.tree.nodesById[id]?.depth ?? 0);
    const depth = scope === "" && roots.length > 0 ? Math.min(...roots) : flag.scopePath.length;
    return draft.spine[Math.min(depth, draft.spine.length - 1)]?.attr ?? null;
  };

  // Held in a ref so source changes cannot re-stamp; the first effect run must not rearrange the pog.
  const previewSourceRef = useRef({
    blockingFixLayout,
    regenerated,
    regenLayout,
    resolverHasChanges: resolver.hasChanges,
    resolverLayout: resolver.layout,
    layout,
    items,
    originalPog,
  });
  previewSourceRef.current = {
    blockingFixLayout,
    regenerated,
    regenLayout,
    resolverHasChanges: resolver.hasChanges,
    resolverLayout: resolver.layout,
    layout,
    items,
    originalPog,
  };

  /** The pog this panel last stamped, so an external replacement is distinguishable from ours. */
  const lastStampedPogRef = useRef<Pog | null>(null);
  const [applyTick, setApplyTick] = useState(0);
  const applyPreview = () => {
    setPreviewOn(true);
    setApplyTick((tick) => tick + 1);
  };

  const previewWasOn = useRef(previewOn);
  const previewMounted = useRef(false);
  useEffect(() => {
    if (!previewMounted.current) {
      previewMounted.current = true;
      previewWasOn.current = previewOn;
      return;
    }
    if (previewOn) {
      const source = previewSourceRef.current;
      const layoutToApply =
        source.blockingFixLayout?.layout ??
        (source.regenerated
          ? source.regenLayout
          : source.resolverHasChanges
            ? source.resolverLayout
            : source.layout);
      const stamped = layoutToPog(
        source.originalPog,
        layoutToApply,
        source.items,
        0,
        source.blockingFixLayout?.unranged ?? [],
      );
      lastStampedPogRef.current = stamped;
      setPog(stamped);
    } else if (previewWasOn.current) {
      lastStampedPogRef.current = null;
      setPog(originalPog);
    }
    previewWasOn.current = previewOn;
  }, [previewOn, applyTick, setPog, originalPog]);

  // Someone else replaced the plan (Revert, page Undo, an item-panel edit). Stand down: drop
  // the preview and the resolver's applied steps, or the next apply re-stamps a stale fix.
  useEffect(() => {
    if (!shouldStandDown(previewOn, pog, originalPog, lastStampedPogRef.current)) return;
    lastStampedPogRef.current = null;
    setPreviewOn(false);
    setRegenerated(false);
    setBlockingFixLayout(null);
    resolverRef.current.reset();
    clearResolverCache(originalPog);
  }, [pog]);

  /** Dirty means something would be LOST by closing; anything that survives the close is not. */
  const isDirty = studioIsDirty({
    undoDepth: session.undoDepth,
    resolverHasChanges: resolver.hasChanges,
    regenerated,
    blockingFixStaged: blockingFixLayout !== null,
    draft,
  });

  // Tab switches keep the preview; close restores the original via the persisted flag.
  const closeStudio = () => {
    if (consumePreviewOnClose(pogAtom as object, previewOn)) {
      setPog(originalPog);
      setPreviewOnState(false);
    }
    clearResolverCache(originalPog);
    clearStudioHistory(studioHistoryKey(basePogId));
    onClose?.();
  };

  const shelfMetres =
    runs.reduce((total, run) => total + run.widthCm * run.shelves.length, 0) / 100;

  const applyMoveResult = (result: { draft: StudioDraft; report: MoveReport }) => {
    commit(result.draft);
    applyPreview();
  };

  const commitHierarchyMove = (result: { draft: StudioDraft; report: MoveReport }) => {
    const question = budgetQuestionOf(result.report, shelfMetres);
    if (!question) {
      applyMoveResult(result);
      return;
    }
    openConfirmModal({
      title: t(
        question.kind === "facings" ? "moveReport.budgetTitle" : "moveReport.budgetFillTitle",
      ),
      description:
        question.kind === "facings"
          ? t("moveReport.budgetDescription", {
              facings: question.facingsLost,
              pct: question.pct,
              metres: question.metres,
            })
          : t("moveReport.budgetFillDescription", {
              pts: (question.fill ? question.fill.before - question.fill.after : 0).toFixed(1),
              before: question.fill?.before ?? 0,
              after: question.fill?.after ?? 0,
              metres: question.metres,
            }),
      icon: { name: "alert", color: Color.yellow },
      buttons: [
        {
          id: "cancel",
          label: t("moveReport.budgetCancel"),
          color: "primary",
          variant: "inverted",
        },
        {
          id: "proceed",
          label: t("moveReport.budgetProceed"),
          color: "yellow",
          onClick: () => applyMoveResult(result),
        },
      ],
    });
  };

  const sortedFlags = useMemo(
    () =>
      [...flags].sort(
        (a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity),
      ),
    [flags],
  );

  const restrictionOffers = useMemo(
    () => buildRestrictionOffers(items, runs, draft),
    [items, runs, draft],
  );

  const keptOffers = draft.keptPlacementOffers ?? [];
  const displayOffers = useMemo(() => {
    const keptIds = new Set(
      keptOffers.map((offer) => placementRuleIdOf(offer.bandVariant, offer.placement)),
    );
    return [
      ...keptOffers,
      ...restrictionOffers.filter(
        (offer) => !keptIds.has(placementRuleIdOf(offer.bandVariant, offer.placement)),
      ),
    ];
  }, [keptOffers, restrictionOffers]);

  const ruleDecisionOf = (offer: (typeof restrictionOffers)[number]) =>
    draft.placementRuleDecisions?.[placementRuleIdOf(offer.bandVariant, offer.placement)];

  const keptDisplayOffers = useMemo(
    () =>
      displayOffers.filter(
        (offer) =>
          draft.placementRuleDecisions?.[placementRuleIdOf(offer.bandVariant, offer.placement)] ===
          "keep",
      ),
    [displayOffers, draft.placementRuleDecisions],
  );
  const openDisplayOffers = useMemo(
    () => displayOffers.filter((offer) => !keptDisplayOffers.includes(offer)),
    [displayOffers, keptDisplayOffers],
  );
  const selectedPlacementOffer = useMemo(
    () =>
      selectedFlagId
        ? displayOffers.find(
            (offer) => placementRuleIdOf(offer.bandVariant, offer.placement) === selectedFlagId,
          )
        : undefined,
    [displayOffers, selectedFlagId],
  );
  const selectedException = selectedExceptionId
    ? inference.exceptions.find((exception) => exception.id === selectedExceptionId)
    : undefined;
  useEffect(() => {
    const skuIds = selectedFlagId
      ? (activeFlags.find((flag) => flag.id === selectedFlagId)?.skuIds ??
        selectedPlacementOffer?.skuIds ??
        [])
      : selectedException
        ? selectedException.skuIds.length > 0
          ? selectedException.skuIds
          : selectedException.scopeKey
            ? session.skuIdsUnderNode(selectedException.scopeKey)
            : []
        : selectedNodeId
          ? session.skuIdsUnderNode(selectedNodeId)
          : [];
    const skuSet = new Set(skuIds);
    const uniqueIds: string[] = [];
    for (const bay of pog.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const item of shelf.items) {
          if (skuSet.has(item.productCode || item.id)) uniqueIds.push(item.uniqueId);
        }
      }
    }
    setHighlights((current) => (sameHighlightIds(current, uniqueIds) ? current : uniqueIds));
  }, [
    selectedFlagId,
    selectedNodeId,
    selectedException,
    selectedPlacementOffer,
    activeFlags,
    pog,
    setHighlights,
  ]);
  useEffect(
    () => () => setHighlights((current) => (current.length === 0 ? current : [])),
    [setHighlights],
  );
  const [keptRulesOpen, setKeptRulesOpen] = useState(false);
  const decideRule = (
    offer: (typeof restrictionOffers)[number],
    decision: "keep" | "decline" | undefined,
  ) => {
    const key = placementRuleIdOf(offer.bandVariant, offer.placement);
    const next: Record<string, "keep" | "decline"> = {
      ...(draft.placementRuleDecisions ?? {}),
    };
    if (decision === undefined) delete next[key];
    else next[key] = decision;
    const others = keptOffers.filter(
      (kept) => placementRuleIdOf(kept.bandVariant, kept.placement) !== key,
    );
    let draftNext: typeof draft = { ...draft, placementRuleDecisions: next };
    if (decision === "keep") {
      if (offer.promotion) draftNext = adoptPromotion(draftNext, offer.promotion, items);
      draftNext = { ...draftNext, keptPlacementOffers: [...others, offer] };
    } else {
      if (offer.promotion) draftNext = revertPromotion(draftNext, offer.bandVariant);
      draftNext = { ...draftNext, keptPlacementOffers: others };
    }
    commit(draftNext);
  };

  const gateRefusalCopy = (refusal: string): string => {
    if (refusal === "no rows to add") return t("panel.gateRefusalNoRows");
    if (refusal.startsWith("no category id")) return t("panel.gateRefusalNoCategory");
    if (refusal.includes("have no store codes")) return t("panel.gateRefusalEmptyStoreScope");
    if (refusal.startsWith("cannot append")) return t("panel.gateRefusalCannotRead");
    return t("panel.gateRefusalGeneric", { detail: refusal });
  };

  const openGate = async (offersToWrite: readonly (typeof restrictionOffers)[number][]) => {
    if (offersToWrite.length === 0) return null;
    const owns = gateOwnershipLinesOf(offersToWrite);
    const generation = ++gateGenerationRef.current;
    // Set before the read so a second Finalize cannot slip in behind the question.
    setGateOpen(true);
    setGateFailure(null);

    // Both write paths replace the category, so the old set is never consulted.
    const existing =
      categoryId !== null
        ? { kind: "replaceCategory" as const }
        : {
            kind: "unavailable" as const,
            reason: "missingCategoryId",
          };

    if (generation !== gateGenerationRef.current) return null;

    const contributions: RestrictionContribution[] = offersToWrite.map((offer) => ({
      rows: offer.rows,
      cost: offer.cost,
      conflictInputs: {
        // The scan runs against the FENCE; the band's own cells go in as `keptCells` and are
        // used for the mirror-exclusion check alone.
        placement: offer.fence ?? offer.placement,
        keptCells: offer.placement,
        bandVariant: offer.bandVariant,
        shippedVariants: offer.shippedVariants,
        fixture: offer.fixture,
      },
    }));
    const proposal = proposeRestrictionsForAll(categoryId ?? "", contributions, existing);
    if (proposal.deletions.length > 0) {
      console.debug("[cdt-studio] restriction gate deletions", proposal.deletions);
    }
    if (proposal.supersededGroups.length > 0) {
      console.debug(
        "[cdt-studio] restriction gate supersedes studio-owned groups",
        proposal.supersededGroups,
      );
    }
    console.debug(
      "[cdt-studio] restriction gate writes",
      proposal.payload?.groups.map((group) => ({
        id: group.variant_group_id,
        rules: group.rules.length,
      })) ?? [],
    );
    console.debug("[cdt-studio] restriction gate rows", proposal.rows);

    const description = (
      <Flex column gap="6px" data-testid="cdt-studio-gate-modal">
        {owns.map((line) => (
          <Text
            key={`${line.variant}-${line.shelves}`}
            textAlign="center"
            variant="small2"
            color={Color.textSecondary}
          >
            {t(line.shelfCount === 1 ? "panel.gateOwnsShelf" : "panel.gateOwnsShelves", {
              variant: line.variant,
              shelves: line.shelves,
            })}
          </Text>
        ))}
        <Text textAlign="center" variant="small2" color={Color.textSecondary}>
          {proposal.cost.evictions === 0
            ? t("panel.gateNoCost")
            : t("panel.gateEvicts", {
                evictions: proposal.cost.evictions,
                siblings: proposal.cost.siblingVariants,
              })}
        </Text>
        {proposal.carriedThrough.length > 0 && (
          <Text textAlign="center" variant="small2" color={Color.textSecondary}>
            {t("panel.gateCarried", { count: proposal.carriedThrough.length })}
          </Text>
        )}
        {proposal.refusals.map((refusal) => (
          <Text key={refusal} textAlign="center" variant="small2" color={Color.alertHighText}>
            {gateRefusalCopy(refusal)}
          </Text>
        ))}
      </Flex>
    );

    const canSave = proposal.payload !== null && proposal.payload !== undefined;
    openConfirmModal({
      title: t("panel.gateTitle"),
      description,
      buttons: canSave
        ? [
            { id: "cdt-studio-gate-cancel", label: t("panel.gateCancel"), onClick: cancelGate },
            {
              id: "cdt-studio-gate-ok",
              label: t("panel.gateOk"),
              color: "success",
              // Close the gate first; a later ModalConfirm close would dismiss the generation step.
              onClick: async () => {
                await closeModal("ModalConfirm");
                await agreeGate(proposal);
                return false;
              },
            },
          ]
        : [{ id: "cdt-studio-gate-cancel", label: t("panel.gateCancel"), onClick: cancelGate }],
    });
    return proposal;
  };

  const agreeGate = async (proposal: RestrictionProposal) => {
    const consent = agreeToRestrictions(proposal);
    if (!consent) return;
    const outcome = await applyRestrictionsThenGenerate(
      consent,
      saveVariantRestrictions,
      onGenerate ?? (async () => null),
    );
    setGateOpen(false);
    // The save receipt is console-only (owner, 2026-09-02). A FAILURE is not: it lands on
    // the panel, because by the time it is known the modal that asked is already closing.
    if (outcome.committed.ok) {
      console.debug("[cdt-studio] restrictions saved", outcome.committed.message);
    } else {
      setGateFailure(outcome.committed.error);
    }
  };

  /** Closing the gate after ship leaves a release without its rules; the banner must say so. */
  const cancelGate = () => {
    gateGenerationRef.current += 1;
    declineRestrictions();
    setGateOpen(false);
    setShipState((current) =>
      current.status === "shipped" ? { ...current, rulesUnsaved: true } : current,
    );
    onClose?.();
  };

  const alreadyShipped = shipState.status === "shipped" && shipState.draft === draft;
  const hasFinalizableTree = items.length > 0 && draft.spine.length > 0;
  const finalizeBlocked =
    !hasFinalizableTree || shipState.status === "shipping" || gateOpen || alreadyShipped;

  const downloadCsvs = () => {
    const result = finalizeCdts(
      items,
      draft,
      runs,
      finalizationLayoutOf({
        blockingFixLayout: blockingFixLayout?.layout,
        regenerated,
        regeneratedLayout: regenLayout,
        resolverHasChanges: resolver.hasChanges,
        resolverLayout: resolver.layout,
        sessionLayout: layout,
      }),
    );
    setRestrictionsRefusal(null);
    downloadFile({
      name: "cdt-assignments",
      extension: "csv",
      data: toCdtAssignmentsCsv(result),
    });
    if (result.restrictions.length === 0) return;
    const shippedVariants = [
      ...new Set(
        Object.values(result.contract.itemValues)
          .map((values) => values[0] ?? "")
          .filter((value) => value !== ""),
      ),
    ].sort();
    const file = toVariantRestrictionsFileCsv(
      restrictionsToFileRows(result.restrictions),
      shippedVariants,
      storeCodes,
    );
    if (file.ok) {
      downloadFile({ name: "variant-restrictions", extension: "csv", data: file.csv });
    } else {
      setRestrictionsRefusal(file.refusal);
    }
    downloadFile({
      name: "variant-restrictions-detail",
      extension: "csv",
      data: toRestrictionsDetailCsv(result.restrictions),
    });
  };

  const onFinalize = async (acknowledgeUnplacedCombos = false) => {
    if (finalizeBlocked) return;
    const gatedOffer = displayOffers.find((offer) => ruleDecisionOf(offer) === undefined);
    const offersToWrite =
      keptDisplayOffers.length > 0 ? keptDisplayOffers : gatedOffer ? [gatedOffer] : [];
    const offeredDraft = offersToWrite.reduce(
      (current, offer) =>
        offer.promotion ? applyPromotion(current, offer.promotion, items) : current,
      draft,
    );
    let shipDraft: StudioDraft;
    try {
      shipDraft = adoptMeasuredBands(items, offeredDraft, runs);
    } catch (error) {
      console.error("Could not adopt the measured store bands.", error);
      setShipState({ status: "refused", text: t("panel.storeRulesRequired"), unplaced: [] });
      return;
    }
    const result = finalizeCdts(
      items,
      shipDraft,
      runs,
      finalizationLayoutOf({
        blockingFixLayout: blockingFixLayout?.layout,
        regenerated,
        regeneratedLayout: regenLayout,
        resolverHasChanges: resolver.hasChanges,
        resolverLayout: resolver.layout,
        sessionLayout: layout,
      }),
    );

    const notices: string[] = [];
    if (!result.verification.orderPreserved) notices.push(t("panel.orderChanged"));
    if (result.staleRestrictions.length > 0) {
      notices.push(
        t("panel.staleRules", {
          count: result.staleRestrictions.length,
          rules: staleRuleNamesOf(result.staleRestrictions),
        }),
      );
    }
    setShipNotices(notices);

    if (merchflowId === undefined) {
      setShipState({ status: "refused", text: t("panel.shipNoMerchflow"), unplaced: [] });
      return;
    }

    setShipState({ status: "shipping" });

    // Child planograms use this measurement, so it must match the Studio tree.
    let payload: ReturnType<typeof prepareCompressionPayload>;
    try {
      payload = prepareCompressionPayload({
        result,
        draft: shipDraft,
        items,
        runs,
        basePogId,
      });
    } catch (error) {
      console.error("Could not prepare the store rule set for this CDT.", error);
      setShipState({
        status: "refused",
        text: t("panel.storeRulesRequired"),
        unplaced: [],
      });
      return;
    }

    let shipped;
    try {
      shipped = await shipCdtCompression(merchflowId, payload, acknowledgeUnplacedCombos);
    } catch (error) {
      setShipState({
        status: "refused",
        text: describeShipFailure(error),
        unplaced: unplacedCombosOf(error),
      });
      return;
    }

    const shippedUnplaced = shipped.unplacedCombos ?? [];
    setShipState({
      status: "shipped",
      draft,
      rulesUnsaved: false,
      text:
        shippedUnplaced.length > 0
          ? t("panel.shippedWithGaps", {
              count: shipped.itemCount,
              treeId: shipped.treeId,
              releaseId: shipped.releaseId,
              dropped: unplacedProductCodesOf(shippedUnplaced).length,
            })
          : t("panel.shipped", {
              count: shipped.itemCount,
              treeId: shipped.treeId,
              releaseId: shipped.releaseId,
            }),
    });

    if (offersToWrite.length > 0) {
      await openGate(offersToWrite);
      return;
    }
    onClose?.();
  };

  const acceptVariantFor = (flag: Flag) =>
    cdt0ResolutionOf(items, draft, runs).variantFor(flag.skuIds);

  const acceptFlagHere = (flag: Flag) =>
    acceptFlag(draft, flag, cdt0ResolutionOf(items, draft, runs));

  const isFlagResolved = (flag: Flag): boolean => {
    if (settledFlagIds.has(flag.id)) return true;
    const step = resolver.applied[flag.id];
    if (step?.resolved) return true;
    return resolver.hasChanges && resolver.handles(flag) && resolver.isSatisfied(flag);
  };

  const openFindings = useMemo(() => {
    if (displayedDetection) {
      return [...displayedDetection.flags]
        .sort((a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity))
        .filter((flag) => !settledFlagIds.has(flag.id));
    }
    if (regenerated && regenFlags) {
      return [...regenFlags]
        .sort((a, b) => severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity))
        .filter((flag) => !settledFlagIds.has(flag.id));
    }
    return sortedFlags.filter((flag) => !isFlagResolved(flag));
  }, [
    sortedFlags,
    settledFlagIds,
    resolver.applied,
    resolver.hasChanges,
    regenerated,
    regenFlags,
    displayedDetection,
  ]);

  const liveSeverityCounts = useMemo(() => {
    const counts: Record<FlagSeverity, number> = { blocking: 0, toCheck: 0, toReview: 0 };
    for (const flag of openFindings) counts[flag.severity] += 1;
    return counts;
  }, [openFindings]);

  const skuAnchors = useMemo(() => {
    const anchors = new Map<string, { uniqueId: string; location: string }>();
    pog.planogram.bays.forEach((bay, bayIndex) => {
      bay.shelves.forEach((shelf, shelfIndex) => {
        for (const item of shelf.items) {
          const sku = item.productCode || item.id;
          if (!anchors.has(sku)) {
            anchors.set(sku, {
              uniqueId: item.uniqueId,
              location: t("panel.location", { bay: bayIndex + 1, shelf: shelfIndex + 1 }),
            });
          }
        }
      });
    });
    return anchors;
  }, [pog, t]);

  const suggestionFor = (flag: Flag): string =>
    flag.fixLabel ??
    (resolver.handles(flag)
      ? t(
          `resolver.suggestion.${flag.kind === "orphan" || flag.kind === "split" ? "relocate" : "resequence"}`,
        )
      : t("resolver.suggestion.retag"));

  useEffect(() => {
    const pins: Record<string, CdtStudioPin> = {};
    const cards: Record<string, CdtStudioExceptionCard> = {};
    openFindings.forEach((flag, index) => {
      const anchor = skuAnchors.get(flag.anchorSkuId);
      if (!anchor) return;
      if (!pins[anchor.uniqueId]) {
        pins[anchor.uniqueId] = { number: index + 1, flagId: flag.id };
      }
      cards[flag.id] = {
        number: index + 1,
        kind: flag.kind,
        title: shortFlagTitle(flag.title, flag.scopePath).title,
        detail: flag.detail,
        suggestion: `${t("resolver.suggested")} ${suggestionFor(flag)}`,
        location: anchor.location,
        built: flag.evidence.built ? flag.evidence.built.join(" → ") : null,
        want: flag.evidence.want ? flag.evidence.want.join(" → ") : null,
        // A kind the resolver knows is not enough: without a pin the step is null and the
        // button would vanish on click with nothing done.
        canAutoResolve: isEditable && resolver.handles(flag) && Boolean(pinFor(flag)),
      };
    });
    openDisplayOffers.forEach((offer, offerIndex) => {
      const anchor = offer.anchorSkuId ? skuAnchors.get(offer.anchorSkuId) : undefined;
      if (!anchor || pins[anchor.uniqueId]) return;
      const ruleId = placementRuleIdOf(offer.bandVariant, offer.placement);
      const number = openFindings.length + offerIndex + 1;
      const shelves = offer.placement.cells.map((cell) => cell.shelf).join("–");
      pins[anchor.uniqueId] = { number, flagId: ruleId };
      cards[ruleId] = {
        number,
        kind: "placement-rule",
        title: t("placementRule.title", { variant: offer.bandVariant, shelves }),
        detail: t(offer.treeHolds ? "placementRule.detailTreeHolds" : "placementRule.detail"),
        suggestion: t("placementRule.keep"),
        location: anchor.location,
        built: null,
        want: null,
        canAutoResolve: false,
      };
    });
    setPins((current) => (sameStudioPins(current, pins) ? current : pins));
    setCards((current) =>
      Object.keys(current).length === Object.keys(cards).length &&
      Object.keys(cards).every((id) => current[id]?.number === cards[id]?.number)
        ? current
        : cards,
    );
  }, [openFindings, openDisplayOffers, skuAnchors, setPins, setCards, isEditable, t]);
  useEffect(
    () => () => {
      setPins((current) => (Object.keys(current).length === 0 ? current : {}));
      setCards((current) => (Object.keys(current).length === 0 ? current : {}));
    },
    [setPins, setCards],
  );

  useEffect(() => {
    if (!selectedFlagId || selectionSource !== "panel") return;
    const flag = flags.find((entry) => entry.id === selectedFlagId);
    const anchorSkuId = flag?.anchorSkuId ?? selectedPlacementOffer?.anchorSkuId;
    const anchor = anchorSkuId ? skuAnchors.get(anchorSkuId) : undefined;
    if (!anchor) return;
    document
      .querySelector(`[data-id="${anchor.uniqueId}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }, [selectedFlagId, selectionSource, flags, selectedPlacementOffer, skuAnchors]);

  useEffect(() => {
    if (selectedFlagId) return;
    const skuIds = selectedNodeId
      ? session.skuIdsUnderNode(selectedNodeId)
      : selectedException
        ? selectedException.skuIds
        : [];
    const anchor = skuIds.map((skuId) => skuAnchors.get(skuId)).find(Boolean);
    if (!anchor) return;
    document
      .querySelector(`[data-id="${anchor.uniqueId}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" });
  }, [selectedNodeId, selectedException, selectedFlagId, skuAnchors]);

  useEffect(() => {
    if (!actionRequest) return;
    const flag = activeFlags.find((entry) => entry.id === actionRequest.flagId);
    setActionRequest(null);
    if (!flag || !isEditable) return;
    if (actionRequest.type === "resolve") {
      applySurgicalFix(flag);
    } else if (actionRequest.type === "accept") {
      commit(acceptFlagHere(flag));
      setSelectedFlagId(null);
      setSelectionSource(null);
    } else {
      commit(dismissFlag(draft, flag.id));
      setSelectedFlagId(null);
      setSelectionSource(null);
    }
  }, [actionRequest, activeFlags, draft, isEditable]);

  useEffect(
    () => () => {
      gateGenerationRef.current += 1;
      setSelectedFlagId(null);
      setSelectionSource(null);
    },
    [setSelectedFlagId, setSelectionSource],
  );

  const [inPlaceRefusals, setInPlaceRefusals] = useState<Record<string, true>>({});

  const applySurgicalFix = (flag: Flag) => {
    const step = resolver.resolveFlag(flag, pinFor(flag));
    if (step && step.physicalChange === true) {
      setInPlaceRefusals((current) => {
        if (!current[flag.id]) return current;
        const next = { ...current };
        delete next[flag.id];
        return next;
      });
      applyPreview();
      return;
    }
    setInPlaceRefusals((current) => ({ ...current, [flag.id]: true }));
  };

  const flagActionsFor = (flag: Flag) => {
    const step = resolver.applied[flag.id];
    if (isFlagResolved(flag) || !isEditable) return null;
    const attempted = (step && !step.resolved) || regenerated || inPlaceRefusals[flag.id] === true;
    const alreadyContiguous = resolver.isSatisfied(flag, pinFor(flag));
    return (
      <Flex gap="6px" onClick={(event) => event.stopPropagation()}>
        {resolver.handles(flag) && pinFor(flag) && !attempted && !alreadyContiguous ? (
          <Button
            size="small"
            onClick={() => {
              applySurgicalFix(flag);
            }}
          >
            {t("resolver.autoResolve")}
          </Button>
        ) : (
          flag.fixLabel && (
            <Button
              size="small"
              onClick={() => {
                commit(applyFix(draft, flag));
                applyPreview();
              }}
            >
              {t("evidence.fix")}
            </Button>
          )
        )}
        <Button
          size="small"
          color="yellow"
          variant="borderOnly"
          onClick={() => commit(acceptFlagHere(flag))}
          title={
            acceptVariantFor(flag) === null
              ? t("evidence.acceptNoRow")
              : t("evidence.acceptNamesVariant", { variant: acceptVariantFor(flag) ?? "" })
          }
        >
          {t("evidence.accept")}
        </Button>
        <Button
          size="small"
          variant="borderless"
          onClick={() => commit(dismissFlag(draft, flag.id))}
        >
          {t("evidence.dismiss")}
        </Button>
      </Flex>
    );
  };

  return (
    <Flex column width="100%" height="100%" minHeight="0" data-testid="cdt-studio-panel">
      <Flex
        align="center"
        gap="8px"
        padding="10px 12px"
        borderBottom={`1px solid ${STUDIO_THEME.border}`}
      >
        <Text variant="subtitle2" color={STUDIO_THEME.text}>
          {t("pageTitle")}
        </Text>
        <Text variant="small2" color={STUDIO_THEME.textSecondary}>
          {t("itemsSummary", {
            count: items.length,
            bays: runs.reduce((sum, run) => sum + run.bays, 0),
          })}
        </Text>
        <Flex flexGrow />
        {/* Balance blocks is withdrawn from the toolbar; the algorithm stays in
         * domain/balanceBlocks.ts with its own tests. */}
        <Button
          size="small"
          variant="borderless"
          isDisabled={!session.canUndo || !isEditable}
          name="cdt-studio-panel-undo"
          onClick={() => {
            const followUp = historyPreviewFollowUpFor(
              "undo",
              previewOn,
              session.undoDepth,
              pogAuthorOf(pog, originalPog, lastStampedPogRef.current),
            );
            session.undo();
            // Outside writer owns the pog: step the draft, not the atom.
            if (followUp === "stand-down") {
              lastStampedPogRef.current = null;
              setPreviewOn(false);
              return;
            }
            // Base draft must restore the original pog, not buildLayout(initialDraft).
            if (followUp === "restore-original") {
              setPreviewOn(false);
              setPog(originalPog);
              return;
            }
            if (followUp === "reapply") applyPreview();
          }}
        >
          {t("toolbar.undo")}
        </Button>
        <Button
          size="small"
          variant="borderless"
          isDisabled={!session.canRedo || !isEditable}
          name="cdt-studio-panel-redo"
          onClick={() => {
            const followUp = historyPreviewFollowUpFor(
              "redo",
              previewOn,
              session.undoDepth,
              pogAuthorOf(pog, originalPog, lastStampedPogRef.current),
            );
            session.redo();
            if (followUp === "stand-down") {
              lastStampedPogRef.current = null;
              setPreviewOn(false);
              return;
            }
            if (followUp === "reapply") applyPreview();
          }}
        >
          {t("toolbar.redo")}
        </Button>
        {onClose && (
          <Button
            size="small"
            variant="borderless"
            onClick={() => {
              if (isDirty) {
                openConfirmModal({
                  title: t("panel.closeDirtyTitle"),
                  description: t("panel.closeDirtyDescription"),
                  icon: { name: "alert", color: Color.yellow },
                  buttons: [
                    {
                      id: "keep",
                      label: t("panel.closeKeepEditing"),
                      color: "primary",
                      variant: "inverted",
                    },
                    {
                      id: "discard",
                      label: t("panel.closeDiscard"),
                      color: "yellow",
                      onClick: closeStudio,
                    },
                  ],
                });
                return;
              }
              closeStudio();
            }}
          >
            {t("panel.close")}
          </Button>
        )}
      </Flex>

      <Flex align="center" gap="6px" padding="8px 12px" style={{ flexWrap: "wrap" }}>
        {severityOrder.map((severity) => (
          <SeverityChip key={severity} $severity={severity}>
            {liveSeverityCounts[severity]} {t(`severity.${severity}`)}
          </SeverityChip>
        ))}
        <Flex flexGrow />
        {(resolver.hasChanges || regenerated) && (
          <Button
            size="small"
            variant="borderless"
            isDisabled={!isEditable}
            onClick={() => {
              resolver.reset();
              clearResolverCache(originalPog);
              setRegenerated(false);
              setBlockingFixLayout(null);
              setBlockingFixNote(null);
              setPreviewOn(false);
            }}
          >
            {t("resolver.resetFixes")}
          </Button>
        )}
        <Button size="small" variant="borderless" onClick={() => setDetectTick((tick) => tick + 1)}>
          {t("toolbar.detect")}
        </Button>
        <Button
          size="small"
          variant="borderless"
          isDisabled={!isEditable}
          onClick={() => {
            const displayed = pogToStudioItems([pog], adapterOptions);
            const source = regenerated ? regenLayout : layoutFromItems(displayed.items);
            const fixItems = regenerated ? items : displayed.items;
            const result = fixVerticalBlocking(fixItems, source, runs, draft, {
              minFillRatio: verticalBlockingOptions.minFillRatio,
              preserveRange: verticalBlockingOptions.preserveRange,
              onlyIfBetter: verticalBlockingOptions.onlyIfBetter,
              rules: aestheticsRules,
              verticalBlockShare: verticalBlockingOptions.blockShare,
            });
            if (result.refusedSparse) {
              setBlockingFixNote(
                t("resolver.blockingTooSparse", {
                  fill: Math.round(result.refusedSparse.fillRatio * 100),
                }),
              );
              return;
            }
            if (result.layout === source) {
              setBlockingFixLayout(null);
              setBlockingFixNote(
                t("resolver.blockingNoGain", {
                  scoreBefore: Number.isFinite(result.scoreBefore)
                    ? Math.round(result.scoreBefore)
                    : "—",
                  scoreAfter: Number.isFinite(result.scoreAfter)
                    ? Math.round(result.scoreAfter)
                    : "—",
                }),
              );
              setDetectTick((tick) => tick + 1);
              return;
            }
            setBlockingFixLayout(result);
            setBlockingFixNote(
              t("resolver.blockingFixed", {
                scoreBefore: Number.isFinite(result.scoreBefore)
                  ? Math.round(result.scoreBefore)
                  : "—",
                scoreAfter: Number.isFinite(result.scoreAfter)
                  ? Math.round(result.scoreAfter)
                  : "—",
                added: result.grown.reduce((sum, growth) => sum + growth.addedFacings, 0),
                removed: result.shrunk.reduce((sum, shrink) => sum + shrink.removedFacings, 0),
                skus: new Set([
                  ...result.grown.map((growth) => growth.skuId),
                  ...result.shrunk.map((shrink) => shrink.skuId),
                ]).size,
                ragged: result.raggedAfter.toFixed(0),
                residuals: result.residuals.length,
                unranged: result.unranged.length,
              }),
            );
            applyPreview();
            setDetectTick((tick) => tick + 1);
          }}
        >
          {t("toolbar.fixBlocking")}
        </Button>
        {/* Resolve all is withdrawn — see KNOWN_BUGS.md #2: the batch pass in
         * studio.resolveAll() damages the plan. Per-row Auto-resolve is unaffected. */}
        <Button
          size="small"
          color="success"
          isDisabled={!isEditable || finalizeBlocked}
          onClick={() => void onFinalize()}
          name="cdt-studio-panel-finalize"
        >
          {shipState.status === "shipping" ? t("panel.shipping") : t("toolbar.finalize")}
        </Button>
        <Button
          size="small"
          variant="borderOnly"
          isDisabled={!hasFinalizableTree}
          onClick={downloadCsvs}
          name="cdt-studio-panel-download-csv"
        >
          {t("toolbar.downloadCsv")}
        </Button>
      </Flex>

      {keptDisplayOffers.length > 0 && (
        <Flex column gap="4px" padding="0 12px 6px 12px" data-testid="cdt-studio-kept-rules">
          <Flex>
            <Button
              size="small"
              color="success"
              variant="borderOnly"
              name="cdt-studio-kept-rules-toggle"
              onClick={() => setKeptRulesOpen((open) => !open)}
            >
              {t("placementRule.keptSummary", { count: keptDisplayOffers.length })}
            </Button>
          </Flex>
          {keptRulesOpen &&
            keptDisplayOffers.map((offer) => {
              const ruleId = placementRuleIdOf(offer.bandVariant, offer.placement);
              const shelves = offer.placement.cells.map((cell) => cell.shelf).join("–");
              return (
                <Flex key={ruleId} align="center" gap="8px" data-testid="cdt-studio-kept-rule">
                  <Text variant="small2" color={STUDIO_THEME.text}>
                    {t("placementRule.title", { variant: offer.bandVariant, shelves })} ·{" "}
                    {t("placementRule.keptChip")}
                  </Text>
                  {isEditable && (
                    <>
                      <Button
                        size="small"
                        variant="inverted"
                        name="cdt-studio-rule-change-answer"
                        data-testid="cdt-studio-rule-change-answer"
                        onClick={() => decideRule(offer, undefined)}
                      >
                        {t("placementRule.changeAnswer")}
                      </Button>
                      <Button
                        size="small"
                        variant="inverted"
                        name="cdt-studio-rule-decline"
                        onClick={() => decideRule(offer, "decline")}
                      >
                        {t("placementRule.decline")}
                      </Button>
                    </>
                  )}
                </Flex>
              );
            })}
        </Flex>
      )}

      {(shipState.status === "refused" || shipState.status === "shipped") && (
        <Flex padding="0 12px">
          <Text
            variant="small1"
            color={
              shipState.status === "refused"
                ? STUDIO_THEME.severity.blocking.text
                : STUDIO_THEME.textSecondary
            }
            data-testid="cdt-studio-panel-ship-note"
          >
            {shipState.text}
          </Text>
        </Flex>
      )}

      {restrictionsRefusal !== null && (
        <Flex padding="0 12px" data-testid="cdt-studio-panel-restrictions-refusal">
          <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
            {restrictionsRefusal}
          </Text>
        </Flex>
      )}

      {shipState.status === "shipped" && shipState.rulesUnsaved && (
        <Flex padding="0 12px" data-testid="cdt-studio-panel-rules-unsaved">
          <Text variant="small1" color={STUDIO_THEME.severity.toReview.text}>
            {t("panel.shippedWithoutRules")}
          </Text>
        </Flex>
      )}

      {shipNotices.length > 0 && (
        <Flex column gap="2px" padding="0 12px" data-testid="cdt-studio-panel-ship-notices">
          {shipNotices.map((notice) => (
            <Text key={notice} variant="small1" color={STUDIO_THEME.severity.toReview.text}>
              {notice}
            </Text>
          ))}
        </Flex>
      )}

      {shipState.status === "refused" && shipState.unplaced.length > 0 && (
        <Flex column gap="6px" padding="8px 12px" data-testid="cdt-studio-panel-unplaced-override">
          <Text variant="small1" color={STUDIO_THEME.text}>
            {t("panel.unplacedTitle", {
              count: unplacedProductCodesOf(shipState.unplaced).length,
            })}
          </Text>

          {shipState.unplaced.map((combo) => (
            <Flex column gap="2px" key={`${combo.cdt0}|${combo.cdt1}`}>
              <Text variant="small1" color={STUDIO_THEME.textSecondary}>
                {t("panel.unplacedCombo", { cdt0: combo.cdt0, cdt1: combo.cdt1 })}
              </Text>
              {combo.productCodes.map((productCode) => (
                <Text variant="small1" color={STUDIO_THEME.textSecondary} key={productCode}>
                  {t("panel.unplacedProduct", {
                    name: adapterOptions?.nameOverlay?.[productCode] ?? productCode,
                    productCode,
                  })}
                </Text>
              ))}
            </Flex>
          ))}

          <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
            {t("panel.unplacedConsequence", {
              count: unplacedProductCodesOf(shipState.unplaced).length,
            })}
          </Text>

          <Flex>
            <Button
              size="small"
              variant="borderOnly"
              isDisabled={!isEditable}
              onClick={() => void onFinalize(true)}
              name="cdt-studio-panel-finalize-anyway"
            >
              {t("panel.unplacedContinue")}
            </Button>
          </Flex>
        </Flex>
      )}

      {gateFailure !== null && (
        <Flex padding="0 12px" data-testid="cdt-studio-panel-gate-result">
          <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
            {t("panel.gateSaveFailed", { message: gateFailure })}
          </Text>
        </Flex>
      )}

      {blockingFixNote && (
        <Flex padding="0 12px">
          <Text variant="small1" color={STUDIO_THEME.textSecondary}>
            {blockingFixNote}
          </Text>
        </Flex>
      )}
      <Flex align="center" gap="8px" padding="0 12px 8px 12px">
        <Toggle value={previewOn} setValue={setPreviewOn} isDisabled={!isEditable} />
        <Text variant="small1" color={STUDIO_THEME.text}>
          {t("panel.previewRegenerated")}
        </Text>
      </Flex>

      <Flex padding="0 12px 4px 12px">
        <Text variant="small1" color={STUDIO_THEME.text}>
          {t("panel.inferredFromBasePog")}
        </Text>
      </Flex>
      <StudioTreeCanvas
        tree={tree}
        spine={draft.spine}
        selectedNodeId={selectedNodeId}
        readOnly={!isEditable}
        onSelectNode={(nodeId) => {
          setSelectedNodeId(nodeId);
          setSelectedFlagId(null);
          setSelectionSource(nodeId ? "panel" : null);
        }}
        onLiftLevelHere={(scopeNodeId: string, fromLevelIndex: number, toLevelIndex: number) => {
          const result = applyHierarchyMove(
            items,
            runs,
            draft,
            { kind: "scoped-level", scopeNodeId, from: fromLevelIndex, to: toLevelIndex },
            hierarchyMoveRules,
            displayedBefore,
            renderedProjection,
            aestheticsRules,
            verticalBlockingOptions.blockShare,
          );
          if (!result.report.committed) return;
          commitHierarchyMove(result);
        }}
        onMoveSibling={(node: StudioTreeNode, offset) => {
          const result = applyHierarchyMove(
            items,
            runs,
            draft,
            siblingMoveOf(draft, node, offset),
            hierarchyMoveRules,
            displayedBefore,
            renderedProjection,
            aestheticsRules,
            verticalBlockingOptions.blockShare,
          );
          commitHierarchyMove(result);
        }}
        onMoveLevel={(from, to) => {
          const result = applyHierarchyMove(
            items,
            runs,
            draft,
            { kind: "level", from, to },
            hierarchyMoveRules,
            displayedBefore,
            renderedProjection,
            aestheticsRules,
            verticalBlockingOptions.blockShare,
          );
          commitHierarchyMove(result);
        }}
        poolingStatus={poolingStatus}
      />

      {(openFindings.length > 0 ||
        openDisplayOffers.length > 0 ||
        shownLayoutAssumptions.length > 0) && (
        <FindingsRegion data-testid="cdt-studio-findings-region">
          {openFindings.length > 0 && (
            <Flex flexShrink={0} padding="8px 12px 4px 12px">
              <Text variant="subtitle2" color={STUDIO_THEME.text}>
                {t("panel.findings", { count: openFindings.length })}
              </Text>
            </Flex>
          )}
          <FindingsScroller data-testid="cdt-studio-panel-findings">
            {openDisplayOffers.map((offer) => {
              const ruleId = placementRuleIdOf(offer.bandVariant, offer.placement);
              const decision = ruleDecisionOf(offer);
              const shelves = offer.placement.cells.map((cell) => cell.shelf).join("–");
              const bays = [...new Set(offer.placement.cells.flatMap((cell) => cell.bays))];
              const bayRange =
                bays.length > 1
                  ? `${Math.min(...bays)}–${Math.max(...bays)}`
                  : String(bays[0] ?? "");
              return (
                <FindingRow
                  key={ruleId}
                  role="button"
                  data-testid="cdt-studio-placement-rule"
                  $selected={selectedFlagId === ruleId}
                  onClick={() => {
                    const nextRuleId =
                      selectedFlagId === ruleId && selectionSource === "panel" ? null : ruleId;
                    setSelectedFlagId(nextRuleId);
                    setSelectionSource(nextRuleId ? "panel" : null);
                    setSelectedNodeId(null);
                    setSelectedExceptionId(null);
                  }}
                >
                  <Flex align="center" gap="6px">
                    <SeverityChip $severity="resolved">{t("placementRule.chip")}</SeverityChip>
                    <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                      {t("placementRule.where", { shelves, bays: bayRange })}
                    </Text>
                    {decision === "decline" && (
                      <Text variant="small2" color={STUDIO_THEME.textSecondary} bold>
                        {t("placementRule.declinedChip")}
                      </Text>
                    )}
                  </Flex>
                  <Text variant="small1" color={STUDIO_THEME.text} bold>
                    {t("placementRule.title", { variant: offer.bandVariant, shelves })}
                  </Text>
                  <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                    {t(offer.treeHolds ? "placementRule.detailTreeHolds" : "placementRule.detail")}
                  </Text>
                  {selectedFlagId === ruleId && (
                    <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                      {t("placementRule.cost", {
                        cells: offer.cost.cells,
                        siblings: offer.cost.siblingVariants,
                        evictions: offer.cost.evictions,
                      })}
                      {offer.promotion
                        ? ` ${t("placementRule.promotionNote", {
                            variant: offer.bandVariant,
                            parent: offer.promotion.parentValue,
                          })}`
                        : ""}
                    </Text>
                  )}
                  {isEditable && (
                    <Flex gap="8px" align="center">
                      <Button
                        size="small"
                        color="success"
                        name="cdt-studio-rule-keep"
                        onClick={(event) => {
                          event.stopPropagation();
                          decideRule(offer, "keep");
                        }}
                      >
                        {t("placementRule.keep")}
                      </Button>
                      {decision !== "decline" && (
                        <Button
                          size="small"
                          variant="inverted"
                          name="cdt-studio-rule-decline"
                          onClick={(event) => {
                            event.stopPropagation();
                            decideRule(offer, "decline");
                          }}
                        >
                          {t("placementRule.decline")}
                        </Button>
                      )}
                      {decision !== undefined && (
                        <Button
                          size="small"
                          variant="inverted"
                          name="cdt-studio-rule-change-answer"
                          data-testid="cdt-studio-rule-change-answer"
                          onClick={(event) => {
                            event.stopPropagation();
                            decideRule(offer, undefined);
                          }}
                        >
                          {t("placementRule.changeAnswer")}
                        </Button>
                      )}
                    </Flex>
                  )}
                </FindingRow>
              );
            })}
            {openFindings.map((flag, index) => {
              const step: ResolverStep | undefined = regenerated
                ? undefined
                : resolver.applied[flag.id];
              const stepState = !step
                ? null
                : step.action === "none"
                  ? "dataOnly"
                  : step.partial
                    ? "partial"
                    : "refused";
              const levelAttr = levelAttrForFlag(flag);
              const confidence = levelAttr ? inference.confidenceByAttr[levelAttr] : undefined;
              return (
                <FindingRow
                  key={flag.id}
                  role="button"
                  $selected={selectedFlagId === flag.id}
                  onClick={() => {
                    const nextFlagId =
                      selectedFlagId === flag.id && selectionSource === "panel" ? null : flag.id;
                    setSelectedFlagId(nextFlagId);
                    setSelectionSource(nextFlagId ? "panel" : null);
                    setSelectedNodeId(null);
                    setSelectedExceptionId(null);
                  }}
                >
                  <Flex align="center" gap="6px">
                    {/* Measured against an order the plan never settled, this is a prompt,
                        not a breach: the convention chip below says so in the same row. */}
                    <SeverityChip $severity={confidence?.convention ? "toReview" : "blocking"}>
                      {index + 1} ·{" "}
                      {t(confidence?.convention ? "resolver.needsReview" : "resolver.violation")}
                    </SeverityChip>
                    {stepState === "partial" && (
                      <SeverityChip $severity="toCheck">{t("resolver.partial")}</SeverityChip>
                    )}
                    {stepState === "refused" && (
                      <SeverityChip $severity="blocking">{t("resolver.refused")}</SeverityChip>
                    )}
                    {stepState === "dataOnly" && (
                      <SeverityChip $severity="toReview">{t("resolver.dataOnly")}</SeverityChip>
                    )}
                    {inPlaceRefusals[flag.id] && (
                      <SeverityChip $severity="toCheck">
                        {t("resolver.cannotRepairInPlace")}
                      </SeverityChip>
                    )}
                    {confidence?.convention && (
                      <SeverityChip $severity="toReview">
                        {t("inference.conventionChip", {
                          pct: `${Math.round((confidence.best ?? 0) * 100)}%`,
                        })}
                      </SeverityChip>
                    )}
                    <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                      {flag.levelLabel ? `${flag.levelLabel} · ` : ""}
                      {t(`severity.${flag.severity}`)}
                      {skuAnchors.has(flag.anchorSkuId)
                        ? ` · ${skuAnchors.get(flag.anchorSkuId)?.location}`
                        : ""}
                    </Text>
                  </Flex>
                  {shortFlagTitle(flag.title, flag.scopePath).context && (
                    <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                      {shortFlagTitle(flag.title, flag.scopePath).context}
                    </Text>
                  )}
                  <Text variant="small1" color={STUDIO_THEME.text} bold>
                    {shortFlagTitle(flag.title, flag.scopePath).title}
                  </Text>
                  {(step?.reason || inPlaceRefusals[flag.id]) && (
                    <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                      {step?.reason ?? t("resolver.cannotRepairInPlaceDetail")}
                    </Text>
                  )}
                  {selectedFlagId === flag.id && (
                    <>
                      <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                        {flag.detail}
                      </Text>
                      {flag.evidence.built && (
                        <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                          {t("evidence.built", { order: flag.evidence.built.join(" → ") })}
                        </Text>
                      )}
                      {flag.evidence.want && (
                        <Text variant="small2" color={STUDIO_THEME.text}>
                          {t("evidence.want", { order: flag.evidence.want.join(" → ") })}
                        </Text>
                      )}
                      <Text variant="small2" color={STUDIO_THEME.text}>
                        {t("resolver.suggested")} {suggestionFor(flag)}
                      </Text>
                      {step && step.notes.length > 0 && (
                        <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                          {step.notes.join(" · ")}
                        </Text>
                      )}
                      {flagActionsFor(flag)}
                    </>
                  )}
                </FindingRow>
              );
            })}
            {shownLayoutAssumptions.length > 0 && (
              <Flex padding="8px 0 2px 0">
                <Text variant="subtitle2" color={STUDIO_THEME.text}>
                  {t("inference.exceptionsTitle", { count: shownLayoutAssumptions.length })}
                </Text>
              </Flex>
            )}
            {shownLayoutAssumptions.map((exception: InferenceException, index) => (
              <FindingRow
                key={exception.id}
                role="button"
                $selected={selectedExceptionId === exception.id}
                onClick={() => {
                  const nextExceptionId =
                    selectedExceptionId === exception.id ? null : exception.id;
                  setSelectedExceptionId(nextExceptionId);
                  setSelectedFlagId(null);
                  setSelectionSource(nextExceptionId ? "panel" : null);
                  setSelectedNodeId(null);
                }}
              >
                <Flex align="center" gap="6px">
                  <SeverityChip
                    $severity={ALERT_KINDS.has(exception.kind) ? "toReview" : "toCheck"}
                  >
                    E{index + 1} ·{" "}
                    {ALERT_KINDS.has(exception.kind)
                      ? t("inference.alertChip")
                      : t("inference.exceptionChip")}
                  </SeverityChip>
                  <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                    {t(`inference.kind.${exception.kind}`)}
                  </Text>
                </Flex>
                <Text variant="small1" color={STUDIO_THEME.text} bold>
                  {exception.title}
                </Text>
                {selectedExceptionId === exception.id && (
                  <Text variant="small2" color={STUDIO_THEME.textSecondary}>
                    {exception.detail}
                  </Text>
                )}
              </FindingRow>
            ))}
          </FindingsScroller>
        </FindingsRegion>
      )}
    </Flex>
  );
};

export const CdtStudioPanel = ({
  pogAtom,
  isEditable,
  merchflowId,
  basePogId,
  categoryConfiguration: initialCategoryConfiguration,
  onClose,
  onGenerate,
}: CdtStudioPanelProps) => {
  const { t } = useTranslation("cdtStudio");
  const { data, isLoading, isError } = useStudioAttributesQuery(merchflowId);
  // The host clones this wrapper every planogram render. Compile from the
  // profile *contents*, not the wrapper identity, or the session rebuilds
  // and writes highlight atoms until the tab is closed.
  const profileFingerprint =
    initialCategoryConfiguration.profileJson ??
    JSON.stringify(initialCategoryConfiguration.profile);
  const configuration = useMemo(
    () =>
      data ? namedStudioConfigurationOf(initialCategoryConfiguration.profile, data) : undefined,
    [data, profileFingerprint],
  );

  if (isLoading) {
    return (
      <Flex flexGrow align="center" justify="center">
        <Spinner size="big" label={t("panel.loadingAttributes")} isCentered />
      </Flex>
    );
  }
  if (isError || !data) {
    return (
      <Flex flexGrow align="center" justify="center" padding="16px">
        <Text variant="small1" color={STUDIO_THEME.severity.blocking.text}>
          {t("panel.attributesFailed")}
        </Text>
      </Flex>
    );
  }
  if (!configuration) return null;

  return (
    <StudioPanelSession
      pogAtom={pogAtom}
      isEditable={isEditable}
      merchflowId={merchflowId}
      basePogId={basePogId ?? null}
      categoryId={data.categoryId}
      onClose={onClose}
      onGenerate={onGenerate}
      configuration={configuration}
    />
  );
};
